import React, { useState, useEffect } from 'react';

interface MoneyInputProps {
  value: number;
  onChange: (val: number) => void;
  currencySymbol?: string;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
}

export const MoneyInput: React.FC<MoneyInputProps> = ({
  value,
  onChange,
  currencySymbol = 'R',
  placeholder,
  className = '',
  readOnly = false,
}) => {
  // We keep a local string state to handle the "while typing" formatting (e.g. "1,000,")
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Sync prop value to display value when not focused
  useEffect(() => {
    if (!isFocused) {
      // Explicitly handle 0 to show "0" instead of empty string, but show empty if null/undefined
      if (value === 0) {
        setDisplayValue('0');
      } else {
        setDisplayValue(value ? value.toLocaleString() : '');
      }
    }
  }, [value, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Remove commas and non-numeric chars (except decimal point)
    const rawValue = inputValue.replace(/,/g, '').replace(/[^0-9.]/g, '');

    // Prevent multiple decimals
    if ((rawValue.match(/\./g) || []).length > 1) return;

    const parts = rawValue.split('.');
    const integerPart = parts[0] ?? '';
    parts[0] = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const formatted = parts.join('.');

    setDisplayValue(formatted);

    // Emit number to parent
    const numberValue = parseFloat(rawValue);
    if (!isNaN(numberValue)) {
      onChange(numberValue);
    } else if (rawValue === '') {
      onChange(0);
    }
  };

  // Dynamic Padding: Adjusted for smaller symbol font
  const paddingLeftClass = currencySymbol.length > 1 ? 'pl-9' : 'pl-5';

  return (
    <div className="relative group">
      <div className="absolute inset-y-0 left-0 pl-1.5 flex items-center pointer-events-none">
        <span className={`text-micro font-bold text-slate-400 ${isFocused ? 'text-hr-pink' : ''}`}>
          {currencySymbol}
        </span>
      </div>
      <input
        type="text"
        inputMode="decimal"
        className={`${paddingLeftClass} pr-2 py-1 w-full border border-slate-300 rounded text-micro font-semibold text-slate-800 focus:ring-2 focus:ring-hr-pink focus:border-hr-pink outline-none transition-all shadow-sm group-hover:border-slate-400 tabular-nums ${className}`}
        placeholder={placeholder || '0.00'}
        value={displayValue}
        onChange={handleChange}
        onFocus={(e) => {
          setIsFocused(true);
          e.target.select();
        }}
        onBlur={() => setIsFocused(false)}
        readOnly={readOnly}
      />
    </div>
  );
};
